import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Grid,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { Formik, Form } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

import { formInitialValuesEdit } from '../formik/edit/formModels/formInitialValues';
import { validationSchemaEdit } from '../formik/edit/formModels/validationSchema';
import { formFieldModelEdit } from '../formik/edit/formModels/ausenciaEmpleadoFormModel';
import InputField from '../../../../components/formFields/InputField';
import SelectField from '../../../../components/formFields/selectField';
import AutocompleteField from '../../../../components/formFields/autocompleteField';
import DatePickerField from '../../../../components/formFields/datePickerField';
import { updateEmployeeAbsenceStore } from '../../../../store/employee_absences/thunks';
import { useResponseHandler } from '../../../../hooks/useResponseHandler';
import { useTableState } from '../../../../hooks/useTableState';
import { absenceTypes, approvalStatuses } from '../data/data';

const ABSENCE_TYPES = absenceTypes;

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
  onSuccess: () => void;
  itemToEdit: any;
}

export const EditAusenciaEmpleadoModal = ({ open, setOpen, onSuccess, itemToEdit }: Props) => {
  const dispatch = useDispatch<any>();
  const { handleResponse, ResponseComponents } = useResponseHandler();
  const { pagePagination, rowsPerPage, searchTerm } = useTableState('employeeAbsences');
  const [initialValues, setInitialValues] = useState<any>(formInitialValuesEdit);

  // Select options coming from the table fetch
  const employeesData =
    useSelector((state: any) => state.employeeAbsences.employeeAbsences_employees_select) || [];

  const {
    formId,
    formField: { empleado_id, absence_type, start_date, end_date, description, approval_status },
  } = formFieldModelEdit;

  useEffect(() => {
    if (itemToEdit && open) {
      setInitialValues({
        [empleado_id.name]: employeesData.find(
          (e: any) => e.value === (itemToEdit.empleado?.id || itemToEdit.empleado_id)
        ) || {
          label: itemToEdit.empleado
            ? `${itemToEdit.empleado.name} ${itemToEdit.empleado.last_name}`
            : 'Empleado',
          value: itemToEdit.empleado?.id || itemToEdit.empleado_id,
        },
        [absence_type.name]: ABSENCE_TYPES.find((a) => a.value === itemToEdit.absenceType) || null,
        [start_date.name]: itemToEdit.startDate || '',
        [end_date.name]: itemToEdit.endDate || '',
        [description.name]: itemToEdit.description || '',
        [approval_status.name]:
          approvalStatuses.find((a) => a.value === itemToEdit.approvalStatus) || null,
      });
    }
  }, [itemToEdit, open, employeesData]);

  const handleClose = () => {
    setOpen(false);
  };

  const _handleSubmit = async (values: any, actions: any) => {
    const payload = {
      empleado_id: values[empleado_id.name]?.value,
      absence_type: values[absence_type.name]?.value,
      start_date: values[start_date.name],
      end_date: values[end_date.name],
      description: values[description.name],
      approval_status: values[approval_status.name]?.value,
    };

    const response = await dispatch(
      updateEmployeeAbsenceStore(itemToEdit.id, payload, pagePagination, rowsPerPage, searchTerm, 1)
    );
    await handleResponse(response, 'actualizar ausencia', true);

    if (response?.ok) {
      handleClose();
      onSuccess();
    }
    actions.setSubmitting(false);
  };

  return (
    <>
      {ResponseComponents}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow:
              '0px 12px 24px -4px rgba(145, 158, 171, 0.12), 0px 0px 2px 0px rgba(145, 158, 171, 0.2)',
          },
        }}
      >
        <DialogTitle sx={{ m: 0, p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              bgcolor: 'rgba(25, 118, 210, 0.08)',
              display: 'flex',
              color: '#1976d2',
            }}
          >
            <EditIcon />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Editar Ausencia
            </Typography>
            <Typography variant="body2" sx={{ color: '#32475C99' }}>
              Modifica los detalles de la ausencia del colaborador.
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 16,
              top: 24,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchemaEdit[0]}
          onSubmit={_handleSubmit}
          enableReinitialize
        >
          {({ isSubmitting, isValid, dirty }) => (
            <Form id={formId}>
              <DialogContent
                dividers
                sx={{ borderTop: '1px solid #32475C1F', borderBottom: '1px solid #32475C1F', p: 3 }}
              >
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12 }}>
                    <AutocompleteField
                      name={empleado_id.name}
                      label={empleado_id.label}
                      options={employeesData}
                      fullWidth
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <AutocompleteField
                      name={absence_type.name}
                      label={absence_type.label}
                      options={ABSENCE_TYPES}
                      fullWidth
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <AutocompleteField
                      name={approval_status.name}
                      label={approval_status.label}
                      options={approvalStatuses}
                      fullWidth
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <DatePickerField name={start_date.name} label={start_date.label} />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <DatePickerField name={end_date.name} label={end_date.label} />
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <InputField
                      name={description.name}
                      label={description.label}
                      multiline
                      rows={3}
                      fullWidth
                    />
                  </Grid>
                </Grid>

                <Box
                  sx={{
                    mt: 3,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: 'rgba(25, 118, 210, 0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <InfoOutlinedIcon sx={{ color: '#1976d2' }} fontSize="small" />
                  <Typography variant="body2" sx={{ color: '#1976d2' }}>
                    La edición de ausencias mantendrá el estado de aprobación.
                  </Typography>
                </Box>
              </DialogContent>
              <DialogActions sx={{ p: 3 }}>
                <Button
                  onClick={handleClose}
                  variant="text"
                  color="inherit"
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting || !isValid}
                  startIcon={<SaveIcon />}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                  Guardar Cambios
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </>
  );
};
